import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import PaymentsIcon from "@mui/icons-material/Payments";

import ContentIsNotAvailableByParallelLimit from "core/components/ContentIsNotAvailableByParallelLimit";
import ContentIsNotAvailableBySessionError from "core/components/ContentIsNotAvailableBySessionError";
import ContentIsNotAvailableError from "core/components/ContentIsNotAvailableError";
import PaymentScreen from "core/components/PaymentScreen";
import WaitingScreen from "core/components/WaitingScreen";

// Константы ошибок доступа (аналогично Player.js)
const CONTENT_IS_NOT_AVAILABLE_BY_STREAMS_QUOTA =
  "CONTENT_IS_NOT_AVAILABLE_BY_STREAMS_QUOTA";
const CONTENT_IS_NOT_AVAILABLE_FOR_GUESTS =
  "CONTENT_IS_NOT_AVAILABLE_FOR_GUESTS";
const CONTENT_IS_NOT_AVAILABLE_IN_COUNTRY =
  "CONTENT_IS_NOT_AVAILABLE_IN_COUNTRY";
const CONTENT_IS_NOT_AVAILABLE_BY_SESSIONS_LIMIT =
  "CONTENT_IS_NOT_AVAILABLE_BY_SESSIONS_LIMIT";
const CONTENT_IS_NOT_AVAILABLE_BY_PARALLEL_LIMIT =
  "CONTENT_IS_NOT_AVAILABLE_BY_PARALLEL_LIMIT";
const CONTENT_IS_NOT_AVAILABLE_BY_ACCESS_START_AT =
  "CONTENT_IS_NOT_AVAILABLE_BY_ACCESS_START_AT";
const CONTENT_IS_NOT_AVAILABLE_FOR_FREE_VIEWING =
  "CONTENT_IS_NOT_AVAILABLE_FOR_FREE_VIEWING";
const CONTENT_IS_NOT_AVAILABLE_BY_PRODUCT_PURCHASE_START_AT =
  "CONTENT_IS_NOT_AVAILABLE_BY_PRODUCT_PURCHASE_START_AT";
const CONTENT_IS_NOT_AVAILABLE_BY_BROWSER_DRM_COMPATIBILITY =
  "CONTENT_IS_NOT_AVAILABLE_BY_BROWSER_DRM_COMPATIBILITY";
const CONTENT_UNSUPPORTED_BY_BROWSER = "CONTENT_UNSUPPORTED_BY_BROWSER";
const WATERMARK_REMOVED = "WATERMARK_REMOVED";

/**
 * Компонент для отображения ошибок доступа к книгам
 * Обрабатывает различные типы ошибок и показывает соответствующие сообщения
 */
export default function BookReaderAccessErrors({
  content,
  accessError,
  onClickProductButton,
}) {
  const { t } = useTranslation("arena");

  if (accessError === CONTENT_IS_NOT_AVAILABLE_BY_ACCESS_START_AT) {
    return (
      <ContentIsNotAvailableError
        title={t("contentIsNotAvailable.accessStartAt.title")}
        text={t("contentIsNotAvailable.accessStartAt.text", {
          date: content.accessStartAt,
        })}
        button={null}
        content={content}
        icon={AccessTimeFilledIcon}
      />
    );
  }

  if (accessError === CONTENT_IS_NOT_AVAILABLE_FOR_GUESTS) {
    return (
      <ContentIsNotAvailableError
        title={t("contentIsNotAvailable.onlyUsers.title")}
        text={null}
        button={
          <Button
            href={content.shareUrl + "?signin"}
            variant="contained"
            color="primary"
            target="_blank"
          >
            {t("contentIsNotAvailable.onlyUsers.button")}
          </Button>
        }
        content={content}
      />
    );
  }

  if (accessError === CONTENT_IS_NOT_AVAILABLE_IN_COUNTRY) {
    return (
      <ContentIsNotAvailableError
        title={t("contentIsNotAvailable.invalidCountry.title")}
        text={t("contentIsNotAvailable.invalidCountry.text")}
        button={null}
        content={content}
      />
    );
  }

  if (accessError === CONTENT_IS_NOT_AVAILABLE_BY_STREAMS_QUOTA) {
    return (
      <ContentIsNotAvailableError
        content={content}
        button={null}
        title={t("contentIsNotAvailable.streamsQuota.title")}
        text={t("contentIsNotAvailable.streamsQuota.text")}
      />
    );
  }

  if (
    [
      CONTENT_UNSUPPORTED_BY_BROWSER,
      CONTENT_IS_NOT_AVAILABLE_BY_BROWSER_DRM_COMPATIBILITY,
      WATERMARK_REMOVED,
    ].includes(accessError)
  ) {
    return (
      <ContentIsNotAvailableError
        title={t("contentIsNotAvailable.browserDrmCompatibility.title")}
        text={t("contentIsNotAvailable.browserDrmCompatibility.text")}
        button={null}
        content={content}
      />
    );
  }

  if (accessError === CONTENT_IS_NOT_AVAILABLE_BY_SESSIONS_LIMIT) {
    return <ContentIsNotAvailableBySessionError content={content} />;
  }

  if (accessError === CONTENT_IS_NOT_AVAILABLE_BY_PARALLEL_LIMIT) {
    return <ContentIsNotAvailableByParallelLimit content={content} />;
  }

  if (accessError === CONTENT_IS_NOT_AVAILABLE_FOR_FREE_VIEWING) {
    return (
      <PaymentScreen
        content={content}
        onClickProductButton={onClickProductButton}
      />
    );
  }

  if (accessError === CONTENT_IS_NOT_AVAILABLE_BY_PRODUCT_PURCHASE_START_AT) {
    return <WaitingScreen content={content} />;
  }

  // Общая ошибка доступа или отсутствие токена
  return (
    <ContentIsNotAvailableError
      title={t("contentIsNotAvailable.general.title", "Доступ ограничен")}
      text={
        accessError === "NO_ACCESS_TOKEN"
          ? t(
              "contentIsNotAvailable.general.text",
              "У вас нет доступа к этому контенту"
            )
          : accessError
      }
      button={null}
      content={content}
      icon={PaymentsIcon}
    />
  );
}

BookReaderAccessErrors.propTypes = {
  content: PropTypes.object.isRequired,
  accessError: PropTypes.string.isRequired,
  onClickProductButton: PropTypes.func,
};
